// ============================================================
// App.js
// Root component — top-level SPA routing (post-Pattern 3 / Step P).
//
// Routes:
//   /contact →  ContactPage      (public contact surface: phone/email
//                               routes + message form, no MSAL)
//   /features → FeaturesPage    (public in-depth module features, no MSAL)
//   /security → SecurityPage    (public trust page, no MSAL)
//   /        →  MarketingHomePage (public marketing surface, no MSAL UI —
//                               the full twelve-module story; promoted from
//                               /vision 2026-07-11 per the SEO ADR's swap
//                               condition. The V1 homepage + the V2 filename are both retired.)
//   /free-trial → FreeTrialPage (public lead-capture form, no MSAL)
//   /login   →  LoginPage      (email-first auth entry, post-MSAL
//                               return target via redirectStartPage)
//   /login/org → FederatedLoginReturn  (direct-Entra / federated
//                               sign-in return — Stage 2 dual-MSAL)
//   /redeem  →  RedeemPage     (invitation acceptance)
//   /survey/* → SurveyPage     (anonymous CareVoice public survey)
//   /teams/* →  TeamsEntryPoint (Teams static-tab entry; owns its
//                               own Teams SSO, not RequireAuth-gated.
//                               Splat so the Phase 2 operations pages
//                               render at /teams/<module> — the
//                               parallel of the /app/<module> tree.)
//   /app/*   →  AuthenticatedApp wrapped in <RequireAuth>
//   /vision  →  retired: staticwebapp.config.json answers it with a 301
//                               to / before the SPA loads (2026-09-28).
//   *        →  NotFoundPage   (branded 404, noindex; was a silent
//                               redirect to / until 2026-07-30)
//
// CODE-SPLITTING (SEO Tier 2 Stage 1): only MarketingHomePage is
// eagerly imported — it is the LCP page for the public/marketing
// critical path. Every other route is React.lazy()-loaded behind a
// single <Suspense> boundary, so docx / recharts / pdfjs /
// react-markdown / MSAL hooks / the entire authenticated graph no
// longer ship in the marketing entry chunk. MsalProvider still
// bootstraps at the root in index.js (Stage 2 — deferring that — is
// parked), so App.js itself no longer imports @azure/msal-*; the
// MSAL hooks live in routes/AuthenticatedApp.jsx + components/RequireAuth.
//
// PRERENDERED MARKETING PAGES (2026-09-28): the marketing routes are served
// as ready-made HTML (scripts/prerender-marketing.js) so search engines read
// real content. The lazy marketing pages use lazyWithPreload, and index.js
// awaits preloadMarketingRoute() before its first render, so React swaps
// the ready-made HTML for the live page without a loading-screen flash.
//
// AuthenticatedApp (routes/AuthenticatedApp.jsx) handles role-aware
// rendering and the entitlements + profile bootstrap; RequireAuth
// handles the unauth bounce to /login with a returnTo.
// ============================================================

import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import MarketingHomePage from './pages/MarketingHomePage';
import lazyWithPreload from './utils/lazyWithPreload';
import LoadingScreen from './components/LoadingScreen';
import LiveAnnouncer from './components/a11y/LiveAnnouncer';
import ToastHost from './components/ToastHost';
import { UnsavedChangesGuardProvider } from './context/UnsavedChangesGuardContext';
import './App.css';

// Lazy routes — each becomes its own chunk, fetched on first visit.
// The marketing pages are prerendered, so they preload before first render.
const FreeTrialPage       = lazyWithPreload(() => import('./pages/FreeTrialPage'));
const ContactPage         = lazyWithPreload(() => import('./pages/ContactPage'));
const FeaturesPage        = lazyWithPreload(() => import('./pages/FeaturesPage'));
const SecurityPage        = lazyWithPreload(() => import('./pages/SecurityPage'));
const AccessibilityPage   = lazyWithPreload(() => import('./pages/AccessibilityPage'));
const LoginPage           = lazy(() => import('./pages/LoginPage'));
const FederatedLoginReturn = lazy(() => import('./pages/FederatedLoginReturn'));
const RedeemPage          = lazy(() => import('./pages/RedeemPage'));
const SurveyPage          = lazy(() => import('./pages/SurveyPage'));
const TeamsEntryPoint     = lazy(() => import('./routes/TeamsEntryPoint'));
const RequireAuth         = lazy(() => import('./components/RequireAuth'));
const AuthenticatedApp    = lazy(() => import('./routes/AuthenticatedApp'));
const NotFoundPage        = lazy(() => import('./pages/NotFoundPage'));

// Path → the lazy page to load before the first render. The homepage is
// imported eagerly, so it needs no entry. Keep in step with PAGES in
// scripts/prerender-marketing.js.
const PRELOAD_BY_PATH = {
  '/free-trial': FreeTrialPage,
  '/contact': ContactPage,
  '/features': FeaturesPage,
  '/security': SecurityPage,
  '/accessibility': AccessibilityPage,
};

export function preloadMarketingRoute(pathname) {
  let p = String(pathname || '/').toLowerCase();
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  const page = PRELOAD_BY_PATH[p];
  return page ? page.preload() : Promise.resolve(null);
}

export default function App() {
  return (
    <BrowserRouter>
      {/* The platform's single pair of ARIA live regions (WCAG 4.1.3).
          Mounted here — above every route tree, outside Suspense — so it
          is present and EMPTY before any status message can fire, on the
          public pages, /app and /teams alike. A live region that is
          conditionally rendered alongside its own text is inserted
          already-populated and frequently announces nothing; see
          components/a11y/LiveAnnouncer.jsx. */}
      <LiveAnnouncer />
      {/* The platform's imperative toast surface. Mounted here for the
          same reason as LiveAnnouncer above: it must be reachable from a
          table row or a leaf dialog without prop-drilling toast state
          through every panel. Pages that hold their own <Toast> are
          unaffected. See components/ToastHost.jsx. */}
      <ToastHost />
      {/* Unsaved-changes nav guard — a passive pass-through until a page
          (CareAuditConductPage) registers a guard. Mounted above BOTH
          the /app and /teams trees so ManagerApp's sidebar + Header can
          consult it on either surface. */}
      <UnsavedChangesGuardProvider>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/" element={<MarketingHomePage />} />
          <Route path="/free-trial" element={<FreeTrialPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/org" element={<FederatedLoginReturn />} />
          <Route path="/redeem" element={<RedeemPage />} />
          {/* CareVoice public survey — anonymous, no MSAL context required.
              Served path-based on BOTH hosts: surveys.a2zcarehub.co.uk is a
              bound custom domain on this same SWA (live 2026-08-23) and is
              the canonical base buildSurveyUrl emits; the main-domain form
              keeps working for URLs already in circulation. */}
          <Route path="/survey/:cycleToken" element={<SurveyPage />} />
          <Route path="/survey/:clientSlug/:cycleToken" element={<SurveyPage />} />
          {/* Teams surface entry point — the static tab's contentUrl
              (teams-app/manifest.json) is https://a2zcarehub.co.uk/teams.
              TeamsEntryPoint detects the Teams host + role and renders the
              carer bottom-nav shell or the manager app; a direct browser
              hit redirects to /. NOT wrapped in <RequireAuth>: that gate
              keys on the MSAL session, which does not exist inside the
              Teams iframe, so it would bounce to /login (the broken path)
              before TeamsEntryPoint mounts. TeamsEntryPoint owns its own
              auth via Teams SSO (teams-js getAuthToken) and redirects a
              non-Teams browser hit to / itself, so nothing protected is
              exposed. */}
          <Route path="/teams/*" element={<TeamsEntryPoint />} />
          <Route
            path="/app/*"
            element={
              <RequireAuth>
                <AuthenticatedApp />
              </RequireAuth>
            }
          />
          {/* The help centre lives INSIDE the shells (/app/help, /teams/help)
              so it inherits their auth and chrome; a bare /help typed by a
              user lands on the browser one. */}
          <Route path="/help" element={<Navigate to="/app/help" replace />} />
          {/* Unknown URLs get the branded 404 (noindex) instead of a
              silent redirect to /. NB: the SWA navigationFallback still
              returns HTTP 200 for these (soft-404 at the status level —
              a true 404 status needs an enumerated route table; see
              docs/backlog.md). */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </UnsavedChangesGuardProvider>
    </BrowserRouter>
  );
}
